import classNames from 'classnames'
import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'
import { useNavigate } from '@lib/react-router-dom'

import { useModal } from '@context/ModalsContext'

import { Button } from '@ui/components/Button'

import { isTMTPlatformType } from '@model/PlatformTypeEnum'
import { DemoTradingAccount } from '@model/TradingAccount'

import { createFormField } from '@global/formField/FormField'
import { createFormNumericField } from '@global/formField/FormNumericField'
import { FormTemplate } from '@global/templates/FormTemplate'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useOnLocaleSwitch } from '@hooks/useOnLocaleSwitch'

import { FieldWrapper } from '@shared/components/FieldWrapper'
import { ActionModal } from '@shared/modals/Action.modal'
import { ConfirmModal } from '@shared/modals/Confirm.modal'

import styles from './DemoTradingAccountDepositForm.module.scss'

export interface DemoTradingAccountDepositFormValues {
  transferTo: DemoTradingAccount
  amount: number
}

interface OuterProps {
  demoTradingAccounts: DemoTradingAccount
  handleFormSubmit: (values: DemoTradingAccountDepositFormValues) => void
}

const FormNumericField = createFormNumericField<{ amount: number }>()
const FormField = createFormField<{ currency: string }>()

const DemoTradingAccountDepositFormUI: React.FC<
  FormikProps<DemoTradingAccountDepositFormValues> & OuterProps
> = (props) => {
  const { values, isValid, handleSubmit } = props
  const { transferTo } = values
  useOnLocaleSwitch(() => props.validateForm())

  const navigate = useNavigate()
  const cancelModal = useModal(ActionModal, {
    onConfirm: () => navigate('/dashboard/traders-room/trading-accounts'),
  })
  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()

  const confirmResetModal = useModal(ConfirmModal, {
    title: t('Subscriptions.Attention'),
    description: t('Trading Account.If you confirm this action, your trading activity will reset'),
    primaryLabel: t('Yes'),
    secondaryLabel: t('No'),
  })

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (
      isTMTPlatformType(transferTo.platformType.id) ||
      isTMTPlatformType(transferTo?.parentPlatformType?.id)
    ) {
      confirmResetModal.open({ onConfirm: handleSubmit })
    } else {
      handleSubmit()
    }
  }

  return (
    <React.Fragment>
      <FormTemplate
        title={t('Trading Account.Add Balance to Demo Trading Account New', {
          account: transferTo.name,
        })}
        goBack={cancelModal.open}
      >
        <Form className={styles.wrapper} onSubmit={handleSubmitForm} autoComplete='off'>
          <FieldWrapper direction='row'>
            <FormNumericField
              type='number'
              name='amount'
              label={t('Wallet.Amount')}
              placeholder={t('Wallet.Amount')}
              required
              hint={
                values.transferTo &&
                `${t('Trading Account.Balance')} ${formatMoney(
                  values.transferTo.platformOverview.balance,
                  values.transferTo.currency.id
                )}`
              }
              step={1}
              fullWidth
              error={props.errors.amount}
            />
            <div className={styles.formWithCurrency}>
              <FormField
                name={'currency'}
                label={t('Wallet.Currency')}
                value={values.transferTo.currency.id}
                disabled
              />
            </div>
          </FieldWrapper>
          <div className={classNames(styles.action, 'mt-6')}>
            <Button.Secondary size='lg' onClick={() => cancelModal.open()}>
              {t('Cancel')}
            </Button.Secondary>
            <Button.Primary size='lg' type='submit' disabled={!isValid}>
              {t('Confirm')}
            </Button.Primary>
          </div>
        </Form>
      </FormTemplate>
    </React.Fragment>
  )
}

export const DemoTradingAccountDepositForm = withFormik<
  OuterProps,
  DemoTradingAccountDepositFormValues
>({
  mapPropsToValues: ({ demoTradingAccounts }) => {
    return {
      amount: 0,
      transferTo: demoTradingAccounts,
    }
  },
  handleSubmit: async (values, { props }) => {
    props.handleFormSubmit(values)
  },
  validate: (values) => {
    const errors: FormikErrors<DemoTradingAccountDepositFormValues> = {}
    if (!values.amount) {
      errors.amount = t('Validation.Required')
    }
    if (values.amount < 100 || values.amount > 5000000) {
      errors.amount = t(
        'Validation.Virtual Deposit cannot be smaller than 100 or greater than 5,000,000'
      )
    }
    return errors
  },
  enableReinitialize: true,
  validateOnChange: true,
  isInitialValid: false,
})(DemoTradingAccountDepositFormUI)
